import { clearAuthData } from "../../../store/slices/auth/authSlice";
import { store } from "../../../store/store";
import { baseApi } from "../../helpers/baseApi/origin.baseApi";
import { ILoginRequestResponse, IUser, IUserStats, IUserUsage } from "./types";


const extendedApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    loginRequest: build.mutation<
      ILoginRequestResponse,
      { email: string; password: string }
    >({
      query: ({ email, password }) => ({
        url: "/authentication",
        method: "post",
        data: {
          strategy: "local",
          email,
          password,
        },
      }),
      transformResponse: (response: ILoginRequestResponse, meta, arg) =>
        response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getUserRequest: build.query<
      IUser,
      {
        id?: number;
      }
    >({
      query: ({ id }) => ({
        url: `users/${id}`,
        method: "get",
      }),
      transformResponse: (response: IUser, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    magicLinkLoginRequest: build.mutation<{ email: string }, { email: string }>(
      {
        query: ({ email }) => ({
          url: "/magic-link",
          method: "post",
          data: {
            action: "sendResetPwd",
            value: { email },
          },
        }),
        transformResponse: (response: { email: string }, meta, arg) => response,
        transformErrorResponse: (response, meta, arg) => response,
      }
    ),
    magicLinkLoginSubmit: build.mutation<
      ILoginRequestResponse,
      { token: string }
    >({
      query: ({ token }) => ({
        url: "/magic-link",
        method: "post",
        data: {
          action: "resetPwdLong",
          value: { token },
        },
      }),
      transformResponse: (response: ILoginRequestResponse, meta, arg) =>
        response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    resetPasswordRequest: build.mutation<{ email: string }, { email: string }>({
      query: ({ email }) => ({
        url: "/authManagement",
        method: "post",
        data: {
          action: "sendResetPwd",
          value: { email },
        },
      }),
      transformResponse: (response: { email: string }, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    resetPasswordSubmit: build.mutation<
      null,
      { password: string; token: string }
    >({
      query: ({ password, token }) => ({
        url: "/authManagement",
        method: "post",
        data: {
          action: "resetPwdLong",
          value: {
            token,
            password,
          },
        },
      }),
      transformResponse: (response: { data: any }, meta, arg) => response.data,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    changePasswordSubmit: build.mutation<
      null,
      { password: string; oldPassword: string, email: string }
    >({
      query: ({ password, oldPassword, email }) => ({
        url: "/authManagement",
        method: "post",
        data: {
          action: "passwordChange",
          value: {
            user: { email },
            oldPassword,
            password
          },
        },
      }),
      transformResponse: (response: { data: any }, meta, arg) => response.data,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getUserUsageRequest: build.query<
      IUserUsage,
      {
        id?: number;
      }
    >({
      query: ({ id }) => ({
        url: `/user-usages/${id}`,
        method: "get",
      }),
      transformResponse: (response: IUserUsage, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getUserStatsRequest: build.query<
      IUserStats,
      {
        id?: number;
      }
    >({
      query: ({ id }) => ({
        url: `/user-stats/${id}`,
        method: "get",
      }),
      transformResponse: (response: IUserStats, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginRequestMutation,
  useMagicLinkLoginRequestMutation,
  useResetPasswordRequestMutation,
  useResetPasswordSubmitMutation,
  useMagicLinkLoginSubmitMutation,
  useChangePasswordSubmitMutation,
  useGetUserRequestQuery,
  useGetUserUsageRequestQuery,
  useGetUserStatsRequestQuery,
  endpoints,
} = extendedApi;
